import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { DEMO_SESSION_TOKEN } from '@/lib/demo';
import type { RootState } from '@/store/store';

/**
 * Loads dashboard data with loading/error state and a retry. `load` receives `demo` — true only
 * for the local-dev demo session (/dev/demo-login), so loaders can return sample data instead
 * of calling the API. Callers must guard the sample-data branch with `import.meta.env.DEV`.
 */
export function useDashboardQuery<T>(load: (demo: boolean) => Promise<T>, initial: T, errorMessage: string) {
  const demo = useSelector((state: RootState) => state.auth.accessToken) === DEMO_SESSION_TOKEN;
  const [data, setData] = useState<T>(initial);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Keep the latest loader without re-running the effect when callers pass inline functions.
  const loadRef = useRef(load);
  loadRef.current = load;

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await loadRef.current(demo));
    } catch (err) {
      const apiMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(apiMessage || errorMessage);
    } finally {
      setLoading(false);
    }
  }, [demo, errorMessage]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { data, loading, error, reload };
}
